import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../../models/Paginate/PagesPaginate";
import { ToastSettings } from "../../../utils/ToastSettings";
import { AccessPageService } from "../../../services/Access/AccessPage/AccessPageService";
import { AccessPageModel } from "../../../models/Access/AccessPage/AccessPageModel";

interface ContextProps {
  accessPage: AccessPageModel;
  setAccessPage: Dispatch<SetStateAction<AccessPageModel>>;
  accessPages: AccessPageModel[];
  pagesRegistration: AccessPageModel;
  params: any;
  setParams: Dispatch<SetStateAction<any>>;
  pages: PagesPaginateModel;
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  isLoadingModal: boolean;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  handleList: () => void;
  handleFetch: (data: any) => Promise<{ success: boolean; access: number }>;
  handleDelete: (id: number) => Promise<boolean>;
  handleGet: (filter: any) => void;
}

export const AccessPageContext = createContext<ContextProps>(
  {} as ContextProps
);

interface AccessPageProviderProps {
  children: ReactNode;
}

export const AccessPageProvider = ({ children }: AccessPageProviderProps) => {
  const [accessPage, setAccessPage] = useState<AccessPageModel>(
    {} as AccessPageModel
  );
  const [accessPages, setAccessPages] = useState<AccessPageModel[]>([]);
  const [pagesRegistration, setPagesRegistration] = useState<AccessPageModel>(
    {} as AccessPageModel
  );
  const [params, setParams] = useState<any | null>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingModal, setIsLoadingModal] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  const service = new AccessPageService();
  const location = useLocation();

  const handleList = useCallback(async () => {
    if (!params || !location.pathname.includes("/access/access-pages/list")) {
      return false;
    }

    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.list(params);
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setAccessPages(_Response?.items);
      setPages(_Response?.pagination);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setError("Não foi possível carregar as páginas de acesso.");
    }
  }, [params, location.pathname]);

  const handleGet = useCallback(async (filter: any) => {
    try {
      setIsLoadingModal(true);
      const [_Response, _Error] = await service.list(filter);
      setIsLoadingModal(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setPagesRegistration(_Response?.items?.[0]);
      setPages(_Response?.pagination);
      return setError("");
    } catch (e) {
      setIsLoadingModal(false);
      setError("Não foi possível carregar as informações.");
    }
  }, []);

  const handleFetch = async (data: any) => {
    try {
      const [_Response, _Error, _Code, _Errors] = await service.fetch(data);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors
        );
        return { success: false, access: 0 };
      }

      setError("");
      return { success: true, access: _Response.data.access_page_id };
    } catch (e) {
      setError("Houve um erro ao salvar a página de acesso");
      return { success: false, access: 0 };
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.delete(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message ||
          "Houve um erro ao deletar a página de acesso"
      );
      return false;
    }
  };

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  return (
    <AccessPageContext.Provider
      value={{
        accessPage,
        setAccessPage,
        accessPages,
        params,
        setParams,
        pages,
        setPages,
        isLoading,
        error,
        isLoadingModal,
        pagesRegistration,
        setError,
        handleList,
        handleFetch,
        handleDelete,
        handleGet,
      }}
    >
      {children}
    </AccessPageContext.Provider>
  );
};

export const useAccessPage = () => useContext(AccessPageContext);
